import * as React from "react";
import {
  Button,
  cn,
  Field,
  Info,
  Input,
  Modal,
  SectionTitle,
  Toast as UiToast,
} from "../../shared/ui/index.ts";
import { I } from "../../shared/icons/icons.tsx";
import {
  appNavigate,
  appFocusPatientOnDashboard,
  appOpenPatient,
  registerAppBridge,
} from "../app-bridge.ts";
import { PatientAvatar } from "../../shared/patient/patient-avatar.component.tsx";
import { PatientCombobox } from "../../shared/patient/patient-combobox.component.tsx";
import { ActSelect } from "../../shared/act-select.component.tsx";
import { Select } from "../../shared/select.component.tsx";
import {
  comparePatientsBySearch,
  formatPatientDisplayName,
  patientMatchesSearch,
  patientNameParts,
} from "../../shared/patient/patient-name.ts";
import { normalizeSearchText } from "../../shared/text-search.ts";
import {
  AGENDA_CHAIRS,
  AGENDA_PRACTITIONERS,
  DEFAULT_AGENDA_ACTS,
} from "../../agenda/appointments.ts";
import {
  getNavItems,
  navItemById,
  PATIENT_MODULE_NAV,
  patientModulePath,
} from "../services/navigation.service.ts";
import { getCurrentPracticeId } from "../services/practices.service.ts";
import type { Patient } from "../services/patients.service.ts";
import { patientsService } from "../services/patients.service.ts";
import { usePractice } from "./practice.provider.tsx";
import type {
  AppointmentDraft,
  DialogContextValue,
  PaletteAction,
  PaletteActionItem,
  PaletteItem,
  PaletteModuleItem,
  PalettePatientItem,
  PatientContextValue,
  ProviderProps,
  ToastContextValue,
  ToastInput,
  ToastRecord,
  ToastType,
} from "./interactions.types.ts";

// Palette / notification footers — small kbd pills
const kbdTinyCn =
  "rounded-[3px] border border-sf-border bg-white px-1 py-px font-[inherit] text-[10px] text-sf-text-secondary";

// ====== INTERACTIONS — global toast system, modals, command palette, dropdowns ======

// ---------- Toast System ----------
const ToastCtx = React.createContext<ToastContextValue | null>(null);

function ToastProvider({ children }: ProviderProps) {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([]);
  const idRef = React.useRef(0);

  const push = React.useCallback((toast: ToastInput) => {
    const id = ++idRef.current;
    const t: ToastRecord = {
      duration: 3500,
      ...toast,
      id,
      type: toast.type ?? "info",
    };
    setToasts((ts) => [...ts, t]);
    const duration = t.duration ?? 3500;
    if (duration > 0) {
      setTimeout(
        () => setToasts((ts) => ts.filter((x) => x.id !== id)),
        duration,
      );
    }
    return id;
  }, []);

  const dismiss = React.useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastCtx.Provider value={{ push, dismiss }}>
      {children}
      <div className="pointer-events-none fixed right-6 bottom-6 z-[1000] flex flex-col gap-2">
        {toasts.map((t) => {
          const st = TYPE_STYLES[t.type] || TYPE_STYLES.info;
          return (
            <UiToast
              key={t.id}
              type={t.type === "error" ? "error" : t.type}
              title={t.title}
              message={t.message}
              icon={st.icon}
              onClose={() => dismiss(t.id)}
            />
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}

const TYPE_STYLES: Record<
  ToastType,
  { bg: string; fg: string; accent: string; icon: React.ReactNode }
> = {
  info: {
    bg: "white",
    fg: "var(--sf-text)",
    accent: "var(--sf-primary)",
    icon: <I.Bell size={15} />,
  },
  success: {
    bg: "white",
    fg: "var(--sf-text)",
    accent: "var(--sf-success)",
    icon: <I.Check size={15} />,
  },
  warning: {
    bg: "white",
    fg: "var(--sf-text)",
    accent: "var(--sf-warning)",
    icon: <I.Bell size={15} />,
  },
  error: {
    bg: "white",
    fg: "var(--sf-text)",
    accent: "var(--sf-danger)",
    icon: <I.X size={15} />,
  },
};

function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

// ---------- New Appointment Modal ----------
function NewAppointmentModal({
  open,
  onClose,
  defaults,
}: {
  open: boolean;
  onClose: () => void;
  defaults?: AppointmentDraft;
}) {
  const toast = useToast();
  usePractice();
  const practicePatients = patientsService.list();
  const mode = defaults?.mode ?? "create";
  const [patient, setPatient] = React.useState(defaults?.patient ?? "");
  const [actId, setActId] = React.useState("");
  const [date, setDate] = React.useState(() => inputDateValue(new Date()));
  const [time, setTime] = React.useState("09:00");
  const [endTime, setEndTime] = React.useState("09:30");
  const [chairId, setChairId] = React.useState("");
  const [practitionerId, setPractitionerId] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const acts = React.useMemo(() => defaults?.acts ?? [], [defaults?.acts]);
  const chairs = React.useMemo(
    () => defaults?.chairs ?? [],
    [defaults?.chairs],
  );
  const practitioners = React.useMemo(
    () => defaults?.practitioners ?? [],
    [defaults?.practitioners],
  );
  const duration = React.useMemo(
    () => minutesBetweenTimes(time, endTime),
    [time, endTime],
  );

  React.useEffect(() => {
    if (open) {
      const nextTime = defaults?.time ?? "09:00";
      const nextDuration = defaults?.duration ?? 30;
      setPatient(defaults?.patient ?? "");
      const nextActId = defaults?.actId ?? acts[0]?.id ?? "";
      setActId(nextActId);
      setDate(defaults?.date ?? inputDateValue(new Date()));
      setTime(nextTime);
      setEndTime(addMinutesToTime(nextTime, nextDuration));
      setChairId(defaults?.chairId ?? chairs[0]?.id ?? "");
      setPractitionerId(defaults?.practitionerId ?? practitioners[0]?.id ?? "");
      setNotes(defaults?.notes ?? "");
      setDeleteConfirmOpen(false);
    }
  }, [acts, chairs, defaults, open, practitioners]);

  const selectedActLabel =
    acts.find((act) => act.id === actId)?.label ?? defaults?.motif ?? "Acte";

  const submit = () => {
    if (!patient.trim()) {
      toast.push({
        type: "error",
        title: "Patient requis",
        message: "Sélectionnez ou saisissez un nom de patient.",
      });
      return;
    }
    toast.push({
      type: "success",
      title: mode === "edit" ? "Rendez-vous modifié" : "Rendez-vous créé",
      message: `${patient} — ${selectedActLabel} le ${formatFR(date)} à ${time}.`,
    });
    defaults?.onSubmit?.({
      patient,
      actId,
      date,
      time,
      endTime,
      duration,
      chairId,
      practitionerId,
      notes,
    });
    onClose();
  };

  const removeAppointment = () => {
    defaults?.onDelete?.();
    toast.push({
      type: "success",
      title: "Rendez-vous supprimé",
      message: patient
        ? `${patient} retiré du planning.`
        : "Rendez-vous retiré.",
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === "edit" ? "Modifier le rendez-vous" : "Nouveau rendez-vous"
      }
      subtitle={
        mode === "edit"
          ? "Ajuster les détails de la consultation"
          : "Planifier une consultation pour un patient"
      }
      width={560}
      footer={
        deleteConfirmOpen ? (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Annuler
            </Button>
            <Button variant="danger" onClick={removeAppointment}>
              Supprimer définitivement
            </Button>
          </div>
        ) : (
          <div className="flex w-full items-center gap-2">
            {mode === "edit" && defaults?.onDelete ? (
              <Button
                variant="danger"
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Supprimer
              </Button>
            ) : null}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                Annuler
              </Button>
              <Button onClick={submit}>
                {mode === "edit" ? "Enregistrer" : "Créer le rendez-vous"}
              </Button>
            </div>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-3.5">
        {deleteConfirmOpen ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-[13px] leading-snug text-sf-text"
          >
            <p className="font-semibold text-sf-danger">
              Supprimer ce rendez-vous ?
            </p>
            <p className="mt-1 text-sf-text-muted">
              {patient.trim() ? (
                <>
                  Le rendez-vous de{" "}
                  <strong className="font-semibold">{patient}</strong> (
                  {selectedActLabel} le {formatFR(date)} à {time}) sera retiré
                  du planning. Cette action est irréversible.
                </>
              ) : (
                "Ce rendez-vous sera retiré du planning. Cette action est irréversible."
              )}
            </p>
          </div>
        ) : null}
        <Field label="Patient">
          <PatientCombobox
            patients={practicePatients}
            value={patient}
            onChange={(name) => setPatient(name)}
            autoFocus
          />
        </Field>
        <Field label="Acte">
          <ActSelect
            acts={acts}
            value={actId}
            onChange={(act) => {
              setActId(act.id);
              setEndTime(addMinutesToTime(time, act.duration));
            }}
          />
        </Field>
        <div className="grid grid-cols-2 gap-x-3 gap-y-3.5 sm:grid-cols-4 sm:gap-2.5">
          <Field label="Date">
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Heure">
            <Input
              type="time"
              value={time}
              onChange={(e) => {
                const previousDuration = duration;
                setTime(e.target.value);
                setEndTime(addMinutesToTime(e.target.value, previousDuration));
              }}
            />
          </Field>
          <Field label="Fin">
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </Field>
          <Field label="Durée">
            <div className="flex h-9 items-center rounded-[var(--sf-radius)] border border-[var(--sf-border-strong)] bg-sf-surface-alt px-2.5 text-[13.5px] font-semibold text-sf-text-secondary">
              {duration} min
            </div>
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-2.5">
          <Field label="Fauteuil">
            <Select
              value={chairId}
              onChange={setChairId}
              options={chairs.map((c) => ({ value: c.id, label: c.label }))}
            />
          </Field>
          <Field label="Praticien">
            <Select
              value={practitionerId}
              onChange={setPractitionerId}
              options={practitioners.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </Field>
        </div>
        <Field label="Notes (optionnel)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Informations complémentaires…"
            rows={3}
            className="min-h-[70px] w-full resize-y rounded-[var(--sf-radius)] border border-[var(--sf-border-strong)] bg-[var(--sf-surface)] px-2.5 py-2 text-[13.5px] font-[inherit] text-[var(--sf-text)] outline-none focus:border-[var(--sf-primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_12%,transparent)]"
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------- Patient Modal ----------
function PatientModal({
  open,
  onClose,
  patient,
}: {
  open: boolean;
  onClose: () => void;
  patient: Patient | null;
}) {
  if (!patient) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={formatPatientDisplayName(patient.name)}
      subtitle={`${patient.age} ans · ${patient.sex === "F" ? "Femme" : "Homme"}`}
      width={680}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Fermer
          </Button>
          <Button
            onClick={() => {
              appOpenPatient(patient, "clinical");
              onClose();
            }}
          >
            Ouvrir le dossier complet
          </Button>
        </>
      }
    >
      <div className="mb-[18px] flex items-center gap-3.5">
        <PatientAvatar patient={patient} size={64} ring />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-sf-text-muted">
            {patient.phone || "—"}
          </div>
          <div className="mt-1.5 flex gap-1.5">
            <span className="rounded border border-sf-primary-100 bg-sf-primary-50 px-2 py-[3px] text-[11px] font-semibold text-sf-primary">
              {patient.tag || "Patient"}
            </span>
          </div>
        </div>
      </div>

      <SectionTitle>Informations</SectionTitle>
      <div className="mb-[18px] grid grid-cols-2 gap-2.5">
        <Info
          label="Date de naissance"
          value={`${1990 + ((patientsService.list().findIndex((p) => p.uuid === patient.uuid) + 1) % 10)}-${String(((patientsService.list().findIndex((p) => p.uuid === patient.uuid) + 1) % 12) + 1).padStart(2, "0")}-15`}
        />
        <Info label="Téléphone" value={patient.phone || "—"} />
        <Info
          label="Email"
          value={(() => {
            const { first, last } = patientNameParts(patient.name);
            return `${(first || "patient").toLowerCase()}.${(last || "x").toLowerCase()}@email.fr`;
          })()}
        />
        <Info label="Sécurité sociale" value="1 85 12 75 116 023" />
        <Info label="Orthodontiste référent" value="Dr. Camille Moreau" />
        <Info label="Dernier RDV" value={patient.last || "Aucun"} />
      </div>

      <SectionTitle>Antécédents médicaux</SectionTitle>
      <div className="mb-[18px] flex flex-wrap gap-1.5">
        {["Allergie latex", "Classe II", "Respiration buccale"].map((a) => (
          <span
            key={a}
            className="rounded border border-sf-border bg-sf-surface-2 px-2.5 py-1 text-[11.5px] font-medium text-sf-text-secondary"
          >
            {a}
          </span>
        ))}
      </div>

      <SectionTitle>Traitements en cours</SectionTitle>
      <div className="flex flex-col gap-1.5">
        {[
          {
            name: "Lévothyrox 50 µg",
            dose: "1 cp/jour le matin",
            since: "2024",
          },
          {
            name: "Élastiques classe II",
            dose: "port quotidien",
            since: "2025",
          },
        ].map((t, i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-md border border-sf-border bg-sf-surface-alt px-3 py-2.5"
          >
            <div>
              <div className="text-[13px] font-semibold">{t.name}</div>
              <div className="text-[11.5px] text-sf-text-muted">{t.dose}</div>
            </div>
            <div className="text-[11px] text-sf-text-muted">
              depuis {t.since}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function formatFR(iso: string) {
  const d = dateFromInputValue(iso);
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function inputDateValue(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateFromInputValue(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year!, month! - 1, day!);
}

function minutesBetweenTimes(start: string, end: string): number {
  const startMinutes = timeToMinutes(start);
  let endMinutes = timeToMinutes(end);
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;
  return Math.max(5, endMinutes - startMinutes);
}

function addMinutesToTime(time: string, minutes: number): string {
  const total = (timeToMinutes(time) + minutes) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function timeToMinutes(time: string): number {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

// ---------- Command Palette ⌘K ----------

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[] = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0]!;
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j]!;
      dp[j] =
        a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j]!, dp[j - 1]!);
      prev = tmp;
    }
  }
  return dp[n]!;
}

function fuzzyThreshold(len: number): number {
  if (len <= 2) return 0;
  if (len <= 4) return 1;
  if (len <= 7) return 2;
  return 3;
}

/**
 * Tier-based score for the command palette filter:
 *  4 — label starts with the query
 *  3 — a word in label/sub starts with the query
 *  2 — a word in label is fuzzy-close to the query (edit distance)
 *  1 — a keyword (synonym) matches the query
 *  0 — no match
 */
function scorePaletteItem(
  item: { label: string; sub?: string; keywords?: string[] },
  rawQuery: string,
): number {
  const nq = normalizeSearchText(rawQuery.trim());
  if (!nq) return 4;
  const nLabel = normalizeSearchText(item.label);
  if (nLabel.startsWith(nq)) return 4;
  const labelWords = nLabel.split(/[\s'-]+/).filter(Boolean);
  if (labelWords.some((w) => w.startsWith(nq))) return 3;
  if (item.sub) {
    const subWords = normalizeSearchText(item.sub)
      .split(/[\s'-]+/)
      .filter(Boolean);
    if (subWords.some((w) => w.startsWith(nq))) return 3;
  }
  const thr = fuzzyThreshold(nq.length);
  if (thr > 0 && labelWords.some((w) => levenshtein(w, nq) <= thr)) return 2;
  if (item.keywords) {
    for (const k of item.keywords) {
      const nk = normalizeSearchText(k);
      if (nk.includes(nq)) return 1;
      const kw = nk.split(/[\s'-]+/).filter(Boolean);
      if (kw.some((w) => w.startsWith(nq))) return 1;
    }
  }
  return 0;
}

function CommandPalette({
  open,
  onClose,
  onNavigate,
  onAction,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (to: string) => void;
  onAction: (action: PaletteAction) => void;
}) {
  usePractice();
  const patientCtx = usePatient();
  const selectedPatient = patientCtx.current;
  const recents = patientCtx.recents;
  const [query, setQuery] = React.useState("");
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      // Focus immédiat (sans setTimeout) pour rester dans le geste utilisateur
      // qui a déclenché l'ouverture — nécessaire sur iOS pour que le clavier
      // virtuel s'ouvre tout seul.
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  React.useEffect(() => {
    setHighlight(0);
  }, [query]);

  React.useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const btn = list.querySelector<HTMLElement>(
      `[data-cmdk-index="${highlight}"]`,
    );
    btn?.scrollIntoView({ block: "nearest" });
  }, [open, highlight, query]);

  if (!open) return null;

  const practiceUuid = getCurrentPracticeId();
  const q = query.trim();
  const appModules: PaletteModuleItem[] = getNavItems(practiceUuid).map(
    (n) => ({
      kind: "module",
      id: n.id,
      label: n.label,
      icon: n.icon,
      keywords: n.keywords,
    }),
  );
  const patientModules: PaletteModuleItem[] = selectedPatient
    ? PATIENT_MODULE_NAV.map((m) => {
        const Icn = (I as Record<string, typeof I.Circle>)[m.icon] ?? I.Circle;
        return {
          kind: "module" as const,
          id: m.id,
          label: m.label,
          icon: Icn,
          sub: formatPatientDisplayName(selectedPatient.name),
          patientUuid: selectedPatient.uuid,
          keywords: m.keywords,
        };
      })
    : [];
  const patientSource = q
    ? patientsService
        .list()
        .filter((p) => patientMatchesSearch(p.name, q))
        .sort((a, b) => comparePatientsBySearch(a.name, b.name, q))
        .slice(0, 5)
    : recents.slice(0, 5);
  const patients: PalettePatientItem[] = patientSource.map((p) => ({
    kind: "patient",
    id: p.uuid,
    label: formatPatientDisplayName(p.name),
    sub: `${p.age} ans · ${p.tag}`,
    color: p.color,
  }));
  const actions: PaletteActionItem[] = [
    {
      kind: "action",
      id: "new-patient",
      label: "Ajouter un patient",
      icon: I.UserPlus,
      keywords: [
        "créer patient",
        "nouveau patient",
        "nouveau dossier",
        "inscription",
        "enregistrer patient",
      ],
    },
  ];

  const filter = <
    T extends { label: string; sub?: string; keywords?: string[] },
  >(
    arr: T[],
  ) => {
    if (!q) return arr;
    return arr
      .map((item, originalIndex) => ({
        item,
        originalIndex,
        score: scorePaletteItem(item, q),
      }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score || a.originalIndex - b.originalIndex)
      .map((s) => s.item);
  };

  const groups = [
    { title: "Patients", items: patients },
    { title: "Modules", items: filter(appModules) },
    ...(patientModules.length > 0
      ? [
          {
            title: "Modules du patient",
            items: filter(patientModules),
          },
        ]
      : []),
    { title: "Actions", items: filter(actions) },
  ].filter((g) => g.items.length > 0);

  const total = groups.reduce((s, g) => s + g.items.length, 0);

  const flatItems: { item: PaletteItem; group: string }[] = [];
  for (const g of groups) {
    for (const item of g.items) flatItems.push({ item, group: g.title });
  }
  const safeHighlight = Math.max(0, Math.min(highlight, flatItems.length - 1));

  const triggerItem = (it: PaletteItem) => {
    if (it.kind === "module") {
      if ("patientUuid" in it) {
        onNavigate(patientModulePath(practiceUuid, it.patientUuid, it.id));
      } else {
        const nav = navItemById(it.id, practiceUuid);
        if (nav) onNavigate(nav.path);
      }
    }
    if (it.kind === "patient") {
      const patient = patientsService.list().find((p) => p.uuid === it.id);
      if (patient) onAction({ kind: "patient", patient });
    }
    if (it.kind === "action") onAction({ kind: it.id });
    onClose();
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      const target = flatItems[safeHighlight]?.item;
      if (target) {
        e.preventDefault();
        triggerItem(target);
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[600] flex animate-[fadeIn_0.15s_ease-out] items-start justify-center bg-gray-900/40 px-5 pt-[15vh] pb-5 backdrop-blur-[2px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex w-[580px] max-w-full flex-col overflow-hidden rounded-[10px] bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] motion-safe:animate-[modalIn_0.2s_cubic-bezier(0.16,1,0.3,1)]"
      >
        <div className="flex items-center gap-2.5 border-b border-sf-border px-4 py-3.5">
          <I.Search size={16} className="shrink-0 text-sf-text-muted" />
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Rechercher…"
            className="min-w-0 flex-1 border-0 bg-transparent font-[inherit] text-[14.5px] text-sf-text outline-none"
          />
          <kbd className="rounded border border-sf-border bg-sf-surface-2 p-1 font-[inherit] text-[11px] leading-none text-sf-text-muted">
            esc
          </kbd>
        </div>
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto p-1.5">
          {total === 0 && (
            <div className="py-8 text-center text-[13px] text-sf-text-muted">
              Aucun résultat pour « {query} »
            </div>
          )}
          {(() => {
            let flatIdx = 0;
            return groups.map((g) => (
              <div key={g.title} className="mb-1">
                <div className="px-3 pt-2 pb-1 text-[10.5px] font-bold tracking-wider text-sf-text-muted uppercase">
                  {g.title}
                </div>
                {g.items.map((it: PaletteItem) => {
                  const Icn = "icon" in it ? it.icon : null;
                  const myIdx = flatIdx++;
                  const isHighlighted = myIdx === safeHighlight;
                  return (
                    <button
                      key={`${it.kind}-${it.id}`}
                      data-cmdk-index={myIdx}
                      onMouseEnter={() => setHighlight(myIdx)}
                      onClick={() => triggerItem(it)}
                      type="button"
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-2.5 rounded-md border-0 px-3 py-2 text-left font-[inherit]",
                        isHighlighted
                          ? "bg-sf-primary-100"
                          : "bg-transparent hover:bg-sf-primary-50",
                      )}
                    >
                      {it.kind === "patient" ? (
                        (() => {
                          const p = patientsService
                            .list()
                            .find((x) => x.uuid === it.id);
                          return p ? (
                            <PatientAvatar patient={p} size={24} />
                          ) : (
                            <div
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                              style={{ background: it.color }}
                            >
                              ?
                            </div>
                          );
                        })()
                      ) : Icn ? (
                        <Icn
                          size={16}
                          className="shrink-0 text-sf-text-secondary"
                        />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium text-sf-text">
                          {it.label}
                        </div>
                        {"sub" in it && it.sub ? (
                          <div className="text-[11.5px] text-sf-text-muted">
                            {it.sub}
                          </div>
                        ) : null}
                      </div>
                      <I.ChevronRight
                        size={13}
                        className="shrink-0 text-sf-text-muted"
                      />
                    </button>
                  );
                })}
              </div>
            ));
          })()}
        </div>
        <div className="flex items-center gap-3.5 border-t border-sf-border bg-sf-surface-alt px-3.5 py-2 text-[11px] text-sf-text-muted">
          <span>
            <kbd className={kbdTinyCn}>↑</kbd>{" "}
            <kbd className={kbdTinyCn}>↓</kbd> naviguer
          </span>
          <span>
            <kbd className={kbdTinyCn}>↵</kbd> sélectionner
          </span>
          <span className="ml-auto">Ligam</span>
        </div>
      </div>
    </div>
  );
}

// ---------- Notifications Dropdown ----------
function NotificationsDropdown({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;
  const items = [
    {
      id: 1,
      type: "message",
      title: "Nouveau message",
      desc: "Sophie Bernard vous a envoyé un message",
      time: "il y a 5 min",
      unread: true,
      color: "#fb923c",
      avatar: "SB",
    },
    {
      id: 2,
      type: "rdv",
      title: "Confirmation de RDV",
      desc: "Julien Petit a confirmé son RDV de 10h",
      time: "il y a 22 min",
      unread: true,
      color: "#34d399",
      avatar: "JP",
    },
    {
      id: 3,
      type: "lab",
      title: "Résultats disponibles",
      desc: "Bilan biologique de Mme Garcia reçu",
      time: "il y a 1 h",
      unread: true,
      color: "#a78bfa",
      avatar: "EG",
    },
    {
      id: 4,
      type: "cancel",
      title: "Annulation",
      desc: "Marc Dubois a annulé son RDV de jeudi",
      time: "hier",
      unread: false,
      color: "#60a5fa",
      avatar: "MD",
    },
    {
      id: 5,
      type: "message",
      title: "Nouveau premier contact",
      desc: "Léa Moreau via Doctolib",
      time: "hier",
      unread: false,
      color: "#f87171",
      avatar: "LM",
    },
  ];
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 motion-safe:animate-[fadeIn_0.15s_ease-out]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute top-[calc(var(--shell-topbar-h,var(--topbar-h))+8px)] right-2 left-2 w-auto max-h-[min(70dvh,420px)] motion-safe:animate-[modalIn_0.15s_ease-out] overflow-hidden rounded-[10px] border border-sf-border bg-white shadow-[0_10px_25px_-5px_rgba(17,24,39,0.15)] sm:left-auto sm:right-4 sm:w-[360px] lg:right-[80px]"
      >
        <div className="flex items-center justify-between border-b border-sf-border px-3.5 py-3">
          <div className="text-[13.5px] font-semibold">Notifications</div>
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent font-[inherit] text-xs font-medium text-sf-primary"
          >
            Tout marquer lu
          </button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.map((n) => (
            <div
              key={n.id}
              className={cn(
                "grid cursor-pointer grid-cols-[32px_1fr] gap-2.5 border-t border-sf-border px-3.5 py-3",
                n.unread
                  ? "bg-sf-primary-50 hover:bg-sf-primary-50/90"
                  : "bg-white hover:bg-sf-surface-alt",
              )}
            >
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                style={{ background: n.color }}
              >
                {n.avatar}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <div
                    className={cn(
                      "text-[13px]",
                      n.unread ? "font-semibold" : "font-medium",
                    )}
                  >
                    {n.title}
                  </div>
                  {n.unread && (
                    <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-sf-primary" />
                  )}
                </div>
                <div className="mt-px text-xs text-sf-text-secondary">
                  {n.desc}
                </div>
                <div className="mt-[3px] text-[11px] text-sf-text-muted">
                  {n.time}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- App-level dialog manager (singleton-style hook) ----------
const DialogCtx = React.createContext<DialogContextValue | null>(null);
const PatientCtx = React.createContext<PatientContextValue | null>(null);

function PatientProvider({ children }: ProviderProps) {
  const [current, setCurrent] = React.useState<Patient | null>(null);
  const [recents, setRecents] = React.useState<Patient[]>([]);
  const selectPatient = (p: Patient) => {
    setCurrent(p);
    if (p)
      setRecents((r) => [p, ...r.filter((x) => x.uuid !== p.uuid)].slice(0, 5));
  };
  const clearPatient = () => setCurrent(null);
  React.useEffect(() => {
    registerAppBridge({
      selectPatient,
      clearPatient,
      getCurrentPatient: () => current,
    });
  }, [current]);
  return (
    <PatientCtx.Provider
      value={{ current, recents, selectPatient, clearPatient }}
    >
      {children}
    </PatientCtx.Provider>
  );
}
function usePatient(): PatientContextValue {
  const ctx = React.useContext(PatientCtx);
  if (!ctx) throw new Error("usePatient must be used within PatientProvider");
  return ctx;
}

function DialogProvider({ children }: ProviderProps) {
  const { current: currentPatient } = usePatient();
  const currentPatientRef = React.useRef(currentPatient);
  React.useEffect(() => {
    currentPatientRef.current = currentPatient;
  }, [currentPatient]);

  const [newRdv, setNewRdv] = React.useState({
    open: false,
    defaults: undefined as AppointmentDraft | undefined,
  });
  const [patientModal, setPatientModal] = React.useState<{
    open: boolean;
    patient: Patient | null;
  }>({
    open: false,
    patient: null,
  });
  const [palette, setPalette] = React.useState(false);

  const openNewRdv = (defaults?: string | AppointmentDraft) => {
    const raw =
      typeof defaults === "string"
        ? ({ patient: defaults } as AppointmentDraft)
        : defaults;
    const selectedName = currentPatientRef.current?.name;
    const mode = raw?.mode ?? "create";
    const withPatient: AppointmentDraft =
      mode === "create" && selectedName && !raw?.patient
        ? { ...(raw ?? {}), patient: selectedName }
        : (raw ?? {});
    const filled: AppointmentDraft = {
      ...withPatient,
      acts: withPatient.acts ?? DEFAULT_AGENDA_ACTS,
      chairs: withPatient.chairs ?? AGENDA_CHAIRS,
      practitioners: withPatient.practitioners ?? AGENDA_PRACTITIONERS,
    };
    setNewRdv({ open: true, defaults: filled });
  };
  const closeNewRdv = () => setNewRdv({ open: false, defaults: undefined });
  const openPatient = (patient: Patient | null) =>
    setPatientModal({ open: true, patient });
  const closePatient = () => setPatientModal({ open: false, patient: null });
  const openPalette = () => setPalette(true);
  const closePalette = () => setPalette(false);

  // Global ⌘K
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <DialogCtx.Provider value={{ openNewRdv, openPatient, openPalette }}>
      {children}
      <NewAppointmentModal
        open={newRdv.open}
        onClose={closeNewRdv}
        defaults={newRdv.defaults}
      />
      <PatientModal
        open={patientModal.open}
        onClose={closePatient}
        patient={patientModal.patient}
      />
      <CommandPalette
        open={palette}
        onClose={closePalette}
        onNavigate={(to: string) => {
          appNavigate(to);
          closePalette();
        }}
        onAction={(a: PaletteAction) => {
          closePalette();
          if (a.kind === "patient") {
            appFocusPatientOnDashboard(a.patient);
          }
          if (a.kind === "new-patient") openNewRdv();
        }}
      />
    </DialogCtx.Provider>
  );
}
function useDialog(): DialogContextValue {
  const ctx = React.useContext(DialogCtx);
  if (!ctx) throw new Error("useDialog must be used within DialogProvider");
  return ctx;
}

// Inject keyframes once
(function injectAnim() {
  if (document.getElementById("orthonexus-anim")) return;
  const s = document.createElement("style");
  s.id = "orthonexus-anim";
  s.textContent = `
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes modalIn { from { opacity: 0; transform: translateY(-8px) scale(.98); } to { opacity: 1; transform: none; } }
    @keyframes toastIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: none; } }
    @keyframes editBarIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
  `;
  document.head.appendChild(s);
})();

export const Interactions = {
  ToastProvider,
  useToast,
  NewAppointmentModal,
  PatientModal,
  CommandPalette,
  NotificationsDropdown,
  DialogProvider,
  useDialog,
  PatientProvider,
  usePatient,
};
